import type { ReactNode } from 'react';
import { LOCALES, LOCALE_LABEL } from '@icetus/contracts';
import type { Locale } from '@icetus/contracts';

/**
 * 编辑页外壳：把"编辑一件东西"从浮层换成整块页面。
 *
 * 为什么不再用抽屉/对话框：
 *
 * 商品表单有 5 段、四门语言各 4 个字段、外加一张 5 列规格表。这类内容
 * 在浮层里无论怎么加宽都是"在别人的地方挤出自己的空间"——抽屉盖住列表，
 * 对话框盖住筛选条件，滚动条还特别长。它本质上是**一整页的东西**，
 * 所以让它占一整页。
 *
 * 取舍（相比浮层失去的东西，逐条都有替代）：
 * - 失去遮罩带来的"不会误触背后"：编辑时列表根本不渲染，误触无从谈起。
 * - 失去"边看列表边改"：返回时列表的筛选、页码、滚动位置都还原（见 CatalogPage），
 *   所以只是"离开一下"，不是"重新找一遍"。
 * - 失去焦点陷阱：不再需要。整页只有一个编辑区，Tab 走不出有意义的地方。
 *
 * 顶部工具条吸顶：长表单滚到规格矩阵时，"保存"仍然在视线内。
 */
export function EditorPage({
  title,
  description,
  meta,
  backLabel = '返回列表',
  onBack,
  actions,
  aside,
  narrow,
  children,
}: {
  title: string;
  description?: string;
  /** 标题右侧的状态徽章之类 */
  meta?: ReactNode;
  backLabel?: string;
  onBack: () => void;
  /** 右上角的主操作（保存/取消） */
  actions?: ReactNode;
  /** 右侧预览栏。不传则表单占满 */
  aside?: ReactNode;
  /**
   * 字段少的表单（分类）限宽。
   *
   * 商品字段多，铺满是应该的；分类只有六七个框，拉到 1400px 宽之后
   * 一个「标识」输入框有半个屏那么长，眼睛读一行要横扫过去。
   */
  narrow?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="editor" aria-label={title}>
      <div className="editor__bar">
        <button type="button" className="editor__back" onClick={onBack}>
          <span aria-hidden="true">←</span>
          {backLabel}
        </button>
        <div className="editor__headtext">
          <h2 className="editor__title">{title}</h2>
          {description ? <p className="editor__desc">{description}</p> : null}
        </div>
        {meta ? <div className="editor__meta">{meta}</div> : null}
        {actions ? <div className="editor__actions">{actions}</div> : null}
      </div>

      <div
        className={`editor__body${aside ? '' : ' editor__body--solo'}${narrow ? ' editor__body--narrow' : ''}`}
      >
        <div className="editor__form">{children}</div>
        {aside ? (
          <aside className="editor__aside" aria-label="实时预览">
            {aside}
          </aside>
        ) : null}
      </div>
    </section>
  );
}

/**
 * 语言切换标签。
 *
 * 四门语言竖排堆叠是编辑页里最长的一段（商品 4 段 × 4 个字段 = 16 个框）。
 * 换成标签后一次只填一门，高度直接少四分之三；同时用圆点标出"这门填没填"，
 * 不用逐个点开也能知道翻译进度。
 *
 * 输入不丢：四门语言的数据一直都在同一个 state 里，切标签只是换显示。
 */
export function LocaleTabs({
  value,
  onChange,
  filled,
}: {
  value: Locale;
  onChange: (locale: Locale) => void;
  /** 该语言是否已经有名称。没填的画成灰的，一眼看出还差哪门 */
  filled: Partial<Record<Locale, boolean>>;
}) {
  return (
    <div className="locale-tabs" role="tablist" aria-label="选择要编辑的语言">
      {LOCALES.map((locale) => {
        const active = value === locale;
        const has = filled[locale] === true;
        return (
          <button
            key={locale}
            type="button"
            role="tab"
            aria-selected={active}
            className={`locale-tab${active ? ' is-active' : ''}${has ? '' : ' is-empty'}`}
            onClick={() => onChange(locale)}
          >
            {LOCALE_LABEL[locale]}
            <span className={`locale-tab__dot${has ? ' is-filled' : ''}`} aria-hidden="true" />
            <span className="visually-hidden">{has ? '（已填写）' : '（未填写）'}</span>
          </button>
        );
      })}
    </div>
  );
}

/** 预览栏里的一行「字段名 + 值」，空值显示为占位 */
export function PreviewRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="preview__row">
      <span className="preview__label">{label}</span>
      <span className="preview__value">{children}</span>
    </div>
  );
}
